import React from "react";
import { Clock, Cpu, ChevronRight, Package, User as UserIcon } from "lucide-react";

/**
 * Pending Builds queue for the Technician station.
 *
 * Contract:
 * - `pending` is already sorted OLDEST FIRST by the backend. First row is the
 *   "NEXT BUILD" recommended pick — it is NOT auto-started, the technician
 *   must click OPEN BUILD.
 * - `onOpen(row)` — parent handles the transition into the existing build
 *   interface. This component does not touch Cloudflare, chat, or order status.
 *
 * Visuals reuse the existing Card / border / typography tokens. No new design
 * system.
 */

function formatWaiting(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
}

function pcName(row) {
  if (row.pc_name && row.pc_name.trim()) return row.pc_name;
  // Fall back to a light, honest label derived from GPU/CPU when present — no invented copy.
  const gpu = row.components?.gpu;
  const cpu = row.components?.cpu;
  if (gpu && cpu) return `Custom Build – ${gpu}`;
  if (gpu) return `Custom Build – ${gpu}`;
  if (cpu) return `Custom Build – ${cpu}`;
  return "Custom PC Build";
}

export function PendingBuildsQueue({ pending, onOpen, refreshing }) {
  return (
    <div className="max-w-[1600px] mx-auto p-6" data-testid="pending-builds-queue">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 font-semibold">
            Station 1 · Queue
          </div>
          <div className="mt-1 text-white text-2xl font-semibold tracking-tight" data-testid="queue-title">
            Pending Builds
          </div>
          <div className="mt-1 text-white/50 text-sm">
            {pending.length === 1
              ? "1 order waiting to be assembled."
              : `${pending.length} orders waiting to be assembled.`}
          </div>
        </div>
        <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 font-medium" aria-live="polite">
          {refreshing ? "Refreshing…" : "Auto-refreshing every 10s"}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4" data-testid="queue-list">
        {pending.map((row, idx) => {
          const first = idx === 0;
          return (
            <div
              key={row.id}
              className="rounded-xl border p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6 min-w-0"
              style={{
                borderColor: first ? "rgba(0,229,255,0.45)" : "rgba(255,255,255,0.08)",
                background: first ? "rgba(0,229,255,0.04)" : "#0d0d10",
                boxShadow: first ? "0 0 0 1px rgba(0,229,255,0.15) inset" : "none",
              }}
              data-testid={`queue-row-${idx}`}
              data-order-id={row.id}
            >
              {/* Rank + NEXT badge */}
              <div className="flex items-center gap-3 md:w-40 shrink-0">
                <div
                  className="w-10 h-10 rounded-full border flex items-center justify-center font-semibold text-sm tabular-nums shrink-0"
                  style={{
                    borderColor: first ? "var(--accent)" : "rgba(255,255,255,0.18)",
                    color: first ? "var(--accent)" : "rgba(255,255,255,0.55)",
                    background: first ? "rgba(0,229,255,0.08)" : "transparent",
                  }}
                >
                  {idx + 1}
                </div>
                {first && (
                  <span
                    className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-semibold tracking-[0.22em]"
                    style={{
                      background: "rgba(0,229,255,0.12)",
                      color: "var(--accent)",
                      border: "1px solid rgba(0,229,255,0.4)",
                    }}
                    data-testid="next-build-badge"
                  >
                    NEXT BUILD
                  </span>
                )}
              </div>

              {/* Order info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white text-lg font-semibold font-mono truncate" data-testid={`queue-order-number-${idx}`}>
                    #{row.order_number}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-white/80 text-sm min-w-0">
                  <UserIcon size={14} className="text-white/40 shrink-0" />
                  <span className="truncate" data-testid={`queue-customer-${idx}`}>{row.customer_name || "—"}</span>
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-white/60 text-[13px] min-w-0">
                  <Cpu size={13} className="text-white/40 shrink-0" />
                  <span className="truncate" data-testid={`queue-pcname-${idx}`}>{pcName(row)}</span>
                </div>
              </div>

              {/* Meta pills */}
              <div className="flex items-center gap-4 md:gap-6 shrink-0 flex-wrap">
                <div className="text-left">
                  <div className="flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-white/40 font-medium">
                    <Clock size={11} /> Waiting
                  </div>
                  <div className="mt-1 text-white text-sm font-semibold tabular-nums" data-testid={`queue-waiting-${idx}`}>
                    {formatWaiting(row.waiting_seconds)}
                  </div>
                </div>
                <div className="text-left">
                  <div className="flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-white/40 font-medium">
                    <Package size={11} /> Components
                  </div>
                  <div className="mt-1 text-white text-sm font-semibold tabular-nums" data-testid={`queue-components-${idx}`}>
                    {row.component_count}
                  </div>
                </div>
              </div>

              {/* Open button */}
              <div className="shrink-0 w-full md:w-auto">
                <button
                  type="button"
                  onClick={() => onOpen(row)}
                  data-testid={`queue-open-${idx}`}
                  className="w-full md:w-auto inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg font-semibold text-[12px] tracking-[0.22em] uppercase transition-transform hover:-translate-y-[1px]"
                  style={
                    first
                      ? { background: "var(--accent)", color: "#000" }
                      : {
                          background: "rgba(0,229,255,0.06)",
                          color: "var(--accent)",
                          border: "1px solid rgba(0,229,255,0.35)",
                        }
                  }
                >
                  Open Build <ChevronRight size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
